import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BillingOverview } from '@/api/billing/billing.types';
import { Phone, Clock, DollarSign, BarChart3, MessageSquare } from 'lucide-react';

interface OverviewMetricsProps {
  overview: BillingOverview | null;
  isLoading: boolean;
  onError: (error: string) => void;
}

export const OverviewMetrics = ({ overview, isLoading, onError }: OverviewMetricsProps) => {
  const formatCurrency = (amount: number) => `€${amount.toFixed(2)}`;

  const metrics = [
    {
      title: 'Total Calls',
      icon: Phone,
      value: overview?.summary.totalCalls || 0,
      subtitle: `Avg ${(overview?.summary.averageCallDuration || 0).toFixed(1)} min/call`,
      color: 'text-blue-600'
    },
    {
      title: 'Total Minutes',
      icon: Clock,
      value: overview?.summary.totalMinutes || 0,
      subtitle: `${formatCurrency(overview?.summary.pricePerMinute || 0)}/min`,
      color: 'text-green-600'
    },
    {
      title: 'Messaging Sessions',
      icon: MessageSquare,
      value: overview?.summary.totalMessagingSessions || 0,
      subtitle: `Cost: ${formatCurrency(overview?.summary.totalMessagingCost || 0)}`,
      color: 'text-orange-600'
    },
    {
      title: 'Total Cost',
      icon: DollarSign,
      value: formatCurrency(overview?.summary.totalCost || 0),
      subtitle: `Svc: ${formatCurrency(overview?.summary.totalServiceCost || 0)} | Tel: ${formatCurrency(overview?.summary.totalTwilioCost || 0)} | Msg: ${formatCurrency(overview?.summary.totalMessagingCost || 0)}`,
      color: 'text-purple-600'
    },
    {
      title: 'Success Rate',
      icon: BarChart3,
      value: overview && overview.summary.totalCalls > 0 
        ? `${Math.round(((overview.callsByStatus.completed || 0) / overview.summary.totalCalls) * 100)}%` 
        : '0%',
      subtitle: `${overview?.callsByStatus.completed || 0} completed calls`,
      color: 'text-emerald-600'
    }
  ];

  if (isLoading) {
    return (
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
    >
      {metrics.map((metric, index) => (
        <motion.div
          key={metric.title}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
        >
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {metric.title}
              </CardTitle>
              <metric.icon className={`h-4 w-4 ${metric.color}`} />
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="text-2xl font-bold leading-none">{metric.value}</div>
              <p className="text-xs text-muted-foreground leading-none">
                {metric.subtitle}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </motion.div>
  );
};
